import React, { useEffect, useRef } from 'react';
import { AuthState } from '../utils/auth';
import { useReactQueryNotes } from '../hooks/useReactQueryNotes';
import NotesUI from './NotesUI';

export interface Note {
  note_id: string;
  seconds_from_start: number;
  content: string;
  labels: Record<string, string>;
  user_id: string;
  org_id?: string;
  version: number;
}

export interface YoutubeVod {
  youtube_id: string;
  seconds_from_session_start: number;
}

export interface SessionNote extends Note {
  youtube_id: string;
  global_offset: number; // seconds_from_session_start + seconds_from_start
  version: number;
}

export interface RoundInfo {
  currentRound: number;
  currentLocalTime: number; // Local time in seconds (countdown from 100)
  rounds: Array<{
    round_number: number;
    round_start: number;
    round_end: number;
  }>;
}

interface NotesProps {
  youtube_vods: YoutubeVod[];
  active_youtube_id: string;
  currentTime?: number; // Current video time in seconds
  onExpandedChange?: (isExpanded: boolean) => void;
  roundInfo?: RoundInfo; // Optional round timing information
  onNoteClick?: (globalTime: number, roundNumber?: number, youtubeId?: string) => void; // Optional click callback
  authState: AuthState; // Auth state from useAuthState hook
  // Playback control
  isPlaying?: boolean;
  onPlaybackControl?: (playing: boolean) => void;
  // Freeze timestamp for new notes
  freezeTimeForNewNote?: boolean; // When true, freeze timestamp when user starts typing
}

const Notes: React.FC<NotesProps> = ({ 
  youtube_vods, 
  active_youtube_id, 
  currentTime = 0, 
  roundInfo,
  onNoteClick,
  authState,
  isPlaying,
  onPlaybackControl,
  freezeTimeForNewNote = false
}) => {
  const { 
    sessionNotes, 
    loading, 
    error, 
    createNote, 
    updateNote, 
    deleteNote, 
    addLabelToNote, 
    removeLabelFromNote,
    createError,
    updateError,
    deleteError
  } = useReactQueryNotes({
    youtube_vods,
    active_youtube_id,
    authState
  });

  // Create a ref to store the previous vods string
  const prevVodsRef = useRef<string>('');

  // Reset UI state when youtube_vods change (map changes)
  useEffect(() => {
    // Create a string representation of the vods to detect changes
    const vodsString = JSON.stringify(youtube_vods.map(vod => ({
      youtube_id: vod.youtube_id,
      seconds_from_session_start: vod.seconds_from_session_start
    })));
    
    if (prevVodsRef.current !== vodsString) {
      // Map has changed, dispatch a custom event to reset UI state
      const resetEvent = new CustomEvent('resetNotesUI');
      window.dispatchEvent(resetEvent);
      prevVodsRef.current = vodsString;
    }
  }, [youtube_vods]);

  // Filter notes to visible rounds range when roundInfo is provided
  const visibleSessionNotes = React.useMemo(() => {
    if (!roundInfo || !roundInfo.rounds || roundInfo.rounds.length === 0) return sessionNotes;
    
    // Filter out rounds with zero length
    const validRounds = roundInfo.rounds.filter(r => r.round_end > r.round_start);
    if (validRounds.length === 0) return sessionNotes;

    const minStart = Math.min(...validRounds.map(r => r.round_start));
    const maxEnd = Math.max(...validRounds.map(r => r.round_end));
    return sessionNotes.filter(n => n.global_offset >= minStart && n.global_offset <= maxEnd);
  }, [sessionNotes, roundInfo]);

  return (
    <NotesUI
      sessionNotes={visibleSessionNotes}
      loading={loading}
      error={error}
      onCreateNote={createNote}
      onUpdateNote={updateNote}
      onDeleteNote={deleteNote}
      onAddLabelToNote={addLabelToNote}
      onRemoveLabelFromNote={removeLabelFromNote}
      currentTime={currentTime}
      roundInfo={roundInfo}
      onNoteClick={onNoteClick}
      authState={authState}
      createError={createError}
      updateError={updateError}
      deleteError={deleteError}
      isPlaying={isPlaying}
      onPlaybackControl={onPlaybackControl}
      freezeTimeForNewNote={freezeTimeForNewNote}
    />
  );
};

export default Notes; 